// Lightweight CSS/SVG-based background textures for the custom template editor

export type TextureName = 'linen' | 'parchment' | 'watercolor' | 'dark_vignette' | 'romantic_warm' | 'noise_grain';

const LINEN_SVG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='4' height='4'%3E%3Crect width='4' height='4' fill='%23f5f0e8'/%3E%3Cpath d='M0 0h1v1H0zm2 2h1v1H2z' fill='%23ede5d8' fill-opacity='0.5'/%3E%3C/svg%3E")`;

const PARCHMENT_SVG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100' height='100' fill='%23f4ead5' /%3E%3Crect width='100' height='100' filter='url(%23n)' opacity='0.08'/%3E%3C/svg%3E")`;

const NOISE_SVG = `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.05'/%3E%3C/svg%3E")`;

export interface TextureStyle {
  background: string;
  overlay?: string;
}

export const BACKGROUND_TEXTURES: Record<TextureName, TextureStyle> = {
  linen: {
    background: `${LINEN_SVG}, linear-gradient(135deg, #f5f0e8 0%, #ede5d8 100%)`,
  },
  parchment: {
    background: `${PARCHMENT_SVG}, linear-gradient(180deg, #f4ead5 0%, #e8dcc5 100%)`,
  },
  watercolor: {
    background: `radial-gradient(ellipse at 20% 50%, rgba(244,164,196,0.3) 0%, transparent 50%), radial-gradient(ellipse at 80% 20%, rgba(163,190,240,0.3) 0%, transparent 50%), radial-gradient(ellipse at 50% 80%, rgba(187,222,182,0.2) 0%, transparent 50%), linear-gradient(135deg, #fef7f0 0%, #f0f4f8 100%)`,
  },
  dark_vignette: {
    background: `radial-gradient(ellipse at center, #1a1a2e 0%, #0a0a14 100%)`,
  },
  romantic_warm: {
    background: `radial-gradient(ellipse at 30% 20%, rgba(251,191,146,0.4) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(244,164,196,0.3) 0%, transparent 50%), linear-gradient(135deg, #fff5f5 0%, #fef3e2 50%, #fce7f3 100%)`,
  },
  noise_grain: {
    background: `${NOISE_SVG}, linear-gradient(135deg, #fafaf9 0%, #f5f5f4 100%)`,
  },
};

export function getBackgroundStyleCSS(
  style: string,
  solidColor: string,
  gradientValue: string
): React.CSSProperties {
  switch (style) {
    case 'solid':
      return solidColor.startsWith('linear-gradient')
        ? { background: solidColor }
        : { backgroundColor: solidColor };
    case 'gradient':
      return { background: gradientValue || solidColor };
    case 'textured_paper':
      return { background: BACKGROUND_TEXTURES.parchment.background };
    case 'dark_dramatic':
      return { background: BACKGROUND_TEXTURES.dark_vignette.background };
    case 'romantic_warm':
      return { background: BACKGROUND_TEXTURES.romantic_warm.background };
    case 'vignette':
      return { background: BACKGROUND_TEXTURES.watercolor.background };
    default:
      return solidColor.startsWith('linear-gradient')
        ? { background: solidColor }
        : { backgroundColor: solidColor };
  }
}
